import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../Context';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Tabs,
  Input,
  Button,
  Progress,
  Tag,
  List,
  Empty,
  Spin,
  Modal,
  Tooltip,
  message,
} from 'antd';
import {
  LinkOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LockOutlined,
  HistoryOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  UnlockOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  CheckOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useIsAuthenticated, isAuthenticated } from '../../utils/auth';
import { executeRecaptcha, preloadRecaptcha } from '../../utils/recaptcha';
import './aeoScan.scss';

// ── Types ─────────────────────────────────────────────────────────────────────

type Improvement = {
  signal: string;
  fix: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
};

type ScanResult = {
  scanId: string;
  inputType: 'url' | 'text';
  score: {
    aeoScore: number;
    geoScore: number;
    aeoGrade: 'A' | 'B' | 'C' | 'D' | 'F';
    geoGrade: 'A' | 'B' | 'C' | 'D' | 'F';
    summary: string;
    breakdown: Record<string, number>;
  };
  signals: Record<string, any>;
  suggestions: Improvement[];
  content: {
    title: string;
    wordCount: number;
    siteName: string | null;
    byline: string | null;
  };
};

type HistoryItem = {
  id: string;
  inputType: 'url' | 'text';
  inputUrl: string | null;
  contentTitle: string | null;
  wordCount: number;
  aeoScore: number;
  geoScore: number;
  createdAt: string;
};

// ── Constants & helpers ───────────────────────────────────────────────────────

const API = process.env.REACT_APP_SERVER_URL;
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

const publicHeader = () => ({});

const SIGNAL_LABELS: Record<string, string> = {
  wordCount: 'Word Count',
  headingCount: 'Heading Count',
  entityCount: 'Entity Count',
  hasArticleSchema: 'Article Schema',
  hasFaqSchema: 'FAQ Schema',
  hasVisualFaq: 'FAQ Section',
  hasMetaTitle: 'Meta Title',
  hasMetaDescription: 'Meta Description',
  hasLlmsTxt: 'llms.txt File',
  blockedAiBots: 'Blocked AI Crawlers',
  robotsTxtExists: 'robots.txt File',
  hasToc: 'Table of Contents',
  hasTldr: 'TL;DR Section',
  hasAuthorBio: 'Author Bio',
  hasReadingTime: 'Reading Time',
  isJsRendered: 'JS Rendering',
  hasEmDash: 'Avoid Em-Dashes',
  hasStatsInText: 'Factual Accuracy / Stats',
  weakClaimsCount: 'Avoid Weak Claims',
  urlMatchesH1: 'URL-to-H1 Alignment',
  hasDefinitionBlocks: 'Definition / Callout Blocks',
  hasHeroImage: 'Hero / Featured Image',
  hasDescriptiveAnchorText: 'Descriptive Anchor Text',
  videoSchemaHasDuration: 'Video Schema Duration',
  hasTechArticleSchema: 'TechArticle Schema',
  wordpressDynamicSchemaRisk: 'Dynamic Schema Detection',
};

type QualityTier = {
  grade: string;
  label: string;
  range: string;
  min: number;
  color: string;
  tip: string;
};

const QUALITY_TIERS: QualityTier[] = [
  {
    grade: 'F',
    label: 'Poor',
    range: '0–39',
    min: 0,
    color: '#ef4444',
    tip: 'Focus on core content depth, structure, and AI crawler settings.',
  },
  {
    grade: 'D',
    label: 'Below Average',
    range: '40–54',
    min: 40,
    color: '#f97316',
    tip: 'Improve formatting structure, schema profiles, and readability.',
  },
  {
    grade: 'C',
    label: 'Average',
    range: '55–69',
    min: 55,
    color: '#facc15',
    tip: 'Add structured context lists and enrich E-E-A-T signals.',
  },
  {
    grade: 'B',
    label: 'Good',
    range: '70–84',
    min: 70,
    color: '#a3e635',
    tip: 'Optimized formatting and keywords. Refine E-E-A-T alignment.',
  },
  {
    grade: 'A',
    label: 'Excellent',
    range: '85–100',
    min: 85,
    color: '#22c55e',
    tip: 'Keep content updated — re-verify facts and keep references fresh.',
  },
];

const getQualityTier = (grade: string): QualityTier => {
  const exactMatch = QUALITY_TIERS.find((t) => t.grade === grade);
  if (exactMatch) return exactMatch;
  // Handle modifiers like 'B+' by matching the base letter
  const baseGrade = grade.replace(/[^A-F]/g, '');
  return QUALITY_TIERS.find((t) => t.grade === baseGrade) ?? QUALITY_TIERS[0];
};

// Speedometer circular gauge
const ScoreGauge: React.FC<{ score: number; grade: string }> = ({
  score,
  grade,
}) => {
  const s = Math.max(0, Math.min(100, score));
  const cx = 150,
    cy = 150,
    r = 115,
    sw = 26;
  const toAngle = (v: number) => 180 - (v / 100) * 180;
  const polar = (deg: number, radius: number = r) => {
    const a = (deg * Math.PI) / 180;
    return { x: cx + radius * Math.cos(a), y: cy - radius * Math.sin(a) };
  };
  const arcPath = (a: number, b: number) => {
    const p1 = polar(toAngle(a));
    const p2 = polar(toAngle(b));
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 0 1 ${p2.x} ${p2.y}`;
  };
  const currentTier =
    QUALITY_TIERS.find((t) => t.grade === grade) ?? QUALITY_TIERS[0];
  const segs = QUALITY_TIERS.map((t, i) => {
    const nextMin =
      i < QUALITY_TIERS.length - 1 ? QUALITY_TIERS[i + 1].min : 100;
    const gap = 0.1;
    const startAdj = i === 0 ? 0 : gap / 2;
    const endAdj = i === QUALITY_TIERS.length - 1 ? 0 : gap / 2;
    return { a: t.min + startAdj, b: nextMin - endAdj, c: t.color };
  });
  const needleAngleDeg = toAngle(s);
  const needleAngleRad = (needleAngleDeg * Math.PI) / 180;
  const tip = polar(needleAngleDeg, r - sw / 2 - 4);
  const baseHalf = 7;
  const baseLeft = {
    x: cx + baseHalf * Math.cos(needleAngleRad + Math.PI / 2),
    y: cy - baseHalf * Math.sin(needleAngleRad + Math.PI / 2),
  };
  const baseRight = {
    x: cx + baseHalf * Math.cos(needleAngleRad - Math.PI / 2),
    y: cy - baseHalf * Math.sin(needleAngleRad - Math.PI / 2),
  };
  const needlePath = `M ${baseLeft.x} ${baseLeft.y} L ${tip.x} ${tip.y} L ${baseRight.x} ${baseRight.y} Z`;
  return (
    <svg viewBox="-5 -8 310 178" className="score-gauge-svg">
      <defs>
        <filter id="needleShadow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.4" />
          <feOffset dx="0" dy="2.5" result="off" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.35" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {segs.map((g, i) => (
        <path
          key={i}
          d={arcPath(g.a, g.b)}
          stroke={g.c}
          strokeWidth={sw}
          fill="none"
          strokeLinecap={i === 0 || i === segs.length - 1 ? 'round' : 'butt'}
        />
      ))}
      <path
        d={needlePath}
        fill={currentTier.color}
        filter="url(#needleShadow)"
      />
      <circle
        cx={cx}
        cy={cy}
        r={11}
        fill={currentTier.color}
        filter="url(#needleShadow)"
      />
      <circle cx={cx} cy={cy} r={5} fill="#fff" />
    </svg>
  );
};

const URL_STAGES = [
  { minSec: 0,  label: 'Reading page layout and crawling HTML...' },
  { minSec: 5,  label: 'Extracting TOC, FAQ, schema markup and metadata...' },
  { minSec: 14, label: 'Running AI semantic verifier (E-E-A-T signals)...' },
  { minSec: 23, label: 'Scoring AEO and GEO metrics with Gemini...' },
  { minSec: 42, label: 'Almost done — finalizing score and suggestions...' },
];

const TEXT_STAGES = [
  { minSec: 0,  label: 'Analyzing content structure and headings...' },
  { minSec: 6,  label: 'Verifying statistics, facts and clarity...' },
  { minSec: 14, label: 'Scoring AEO and GEO content metrics with Gemini...' },
  { minSec: 24, label: 'Almost done — finalizing score and suggestions...' },
];

const BUCKETS = [
  { key: 'structure', label: 'Structure & Formatting', icon: '📝' },
  { key: 'schema', label: 'Search Schema & Indexing', icon: '🔍' },
  { key: 'eeat', label: 'E-E-A-T & Authority', icon: '🌟' },
];

const ProgressiveLoadingCard: React.FC<{ mode: 'url' | 'text' }> = ({ mode }) => {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number>(Date.now());
  const stages = mode === 'url' ? URL_STAGES : TEXT_STAGES;
  // Use 65s for URL (scrape~3s + verifier~8s + scorer~40s + buffer)
  // Use 32s for text (no verifier, lighter scorer ~18-22s)
  const expectedTotal = mode === 'url' ? 65 : 32;

  useEffect(() => {
    startRef.current = Date.now();
    const tick = setInterval(
      () => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)),
      400
    );
    return () => clearInterval(tick);
  }, []);

  const currentStage =
    stages.slice().reverse().find((s) => elapsed >= s.minSec) || stages[0];
  const progressPct = Math.min(99, Math.round((elapsed / expectedTotal) * 100));

  const visibleBuckets = mode === 'url' ? BUCKETS : BUCKETS.filter((b) => b.key !== 'schema');
  const activeBucketIdx = Math.min(
    visibleBuckets.length - 1,
    Math.floor(elapsed / Math.max(1, Math.round(expectedTotal / visibleBuckets.length)))
  );

  const DONE_COLOR = '#10b981';
  const ACTIVE_COLOR = '#ff7c02';

  return (
    <div className="aeo-loading-card" style={{ width: '100%', maxWidth: 560, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <Progress
          type="circle"
          percent={progressPct}
          strokeColor={{ '0%': '#ffb03a', '100%': ACTIVE_COLOR }}
          trailColor="#f1f5f9"
          size={96}
          strokeWidth={8}
          format={(p) => (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{p}%</span>
              <span style={{ fontSize: 9, color: '#94a3b8', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Progress
              </span>
            </div>
          )}
        />
        <h3 style={{ margin: '18px 0 8px 0', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
          Auditing AEO/GEO Signals
        </h3>
        <p style={{ fontSize: 13.5, color: '#64748b', margin: 0 }}>
          {currentStage.label}
        </p>
      </div>

      <div
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          background: '#fff',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
        }}
      >
        {visibleBuckets.map((b, i) => {
          const isActive = i === activeBucketIdx;
          const isDone = i < activeBucketIdx;
          const isLast = i === visibleBuckets.length - 1;
          return (
            <div
              key={b.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 18px',
                borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                background: isActive ? `${ACTIVE_COLOR}0a` : 'transparent',
                transition: 'background 0.25s ease',
                position: 'relative',
              }}
            >
              {/* Left: status indicator */}
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDone ? DONE_COLOR : isActive ? `${ACTIVE_COLOR}22` : '#f1f5f9',
                  color: isDone ? '#fff' : isActive ? ACTIVE_COLOR : '#94a3b8',
                  flexShrink: 0,
                  transition: 'all 0.25s ease',
                }}
              >
                {isDone ? (
                  <CheckCircleOutlined style={{ fontSize: 16 }} />
                ) : isActive ? (
                  <Spin size="small" />
                ) : (
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{i + 1}</span>
                )}
              </div>

              {/* Middle: category label */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: isActive ? '#0f172a' : isDone ? '#475569' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{b.icon}</span>
                  <span>{b.label}</span>
                </div>
              </div>

              {/* Right: status text */}
              <div style={{ flexShrink: 0 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: isDone ? DONE_COLOR : isActive ? ACTIVE_COLOR : '#cbd5e1',
                  }}
                >
                  {isDone ? 'Completed' : isActive ? 'In progress' : 'Queued'}
                </span>
              </div>

              {/* Active row: bottom progress line */}
              {isActive && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 2,
                    background: '#f1f5f9',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: '40%',
                      background: ACTIVE_COLOR,
                      animation: 'geoLoaderSlide 1.6s ease-in-out infinite',
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes geoLoaderSlide {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(120%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
};

const AeoScan: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<'url' | 'text'>('url');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const isPaid = useIsAuthenticated();

  const { planDetails, setPlanDetails }: any = useContext(GlobalContext);
  const aeoLimit = planDetails?.aeoScanLimit ?? 0;
  const aeoUsed = planDetails?.aeoScanUsed ?? 0;
  const aeoRemaining = planDetails?.aeoScanRemaining ?? Math.max(aeoLimit - aeoUsed, 0);

  const [unlocked, setUnlocked] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);

  // Auto-redirect authenticated users off public URL.
  useEffect(() => {
    const onPublicRoute = location.pathname.startsWith('/aeo-auditor-public');
    if (isPaid && onPublicRoute && !location.pathname.startsWith('/aeo-auditor')) {
      navigate(`/aeo-auditor${location.search}`, { replace: true });
    }
  }, [isPaid, location.pathname, location.search, navigate]);

  // ── WordPress prefill bridge ─────────────────────────────────────────────
  // When user lands with ?prefillId=<uuid> (redirected from WP form action),
  // fetch the stored {type, content} once, populate the correct tab/input,
  // strip the query param, then trigger the scan automatically.
  const [autoScanCounter, setAutoScanCounter] = useState(0);
  const [prefillHandled, setPrefillHandled] = useState(false);

  useEffect(() => {
    if (id) return; // result-view URL — prefill does not apply
    if (prefillHandled) return;
    // If paid user is on the public route, the auto-redirect effect is about
    // to move them to /app/aeo-score. Skip the prefill fetch here — it would
    // consume the token, then the remount on the paid route would re-fetch
    // and see 410 "already consumed". Let the paid-route mount handle it.
    if (isPaid && location.pathname.startsWith("/aeo-auditor-public") && !location.pathname.startsWith('/aeo-auditor')) return;
    
    const params = new URLSearchParams(location.search);
    const prefillId = params.get("prefillId");
    if (!prefillId) return;
    setPrefillHandled(true);

    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/public/wp-prefill/${prefillId}`
        );
        if (data.type === "url") {
          setActiveTab("url");
          setUrl(data.content);
        } else {
          setActiveTab("text");
          setText(data.content);
        }
        const basePath = isPaid ? "/aeo-auditor" : "/aeo-auditor-public";
        navigate(basePath, { replace: true });
        setAutoScanCounter((c) => c + 1);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 410) {
          message.error("This prefill link has expired or was already used");
        } else if (status === 404) {
          message.error("Prefill link not found");
        } else {
          message.error("Failed to load prefilled content");
        }
        const basePath = isPaid ? "/aeo-auditor" : "/aeo-auditor-public";
        navigate(basePath, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, isPaid, id]);

  useEffect(() => {
    if (autoScanCounter === 0) return;
    handleScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScanCounter]);

  const fetchHistory = async () => {
    if (!isPaid) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/aeo-scans?limit=20`, {
        headers: authHeader(),
      });
      setHistory(res.data?.scans || []);
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    if (!isPaid) preloadRecaptcha();
  }, [isPaid]);

  const loadScanDetails = async (scanId: string) => {
    setDetailsLoading(true);
    const modeAtCall = isAuthenticated() ? 'paid' : 'free';
    try {
      const endpoint =
        modeAtCall === 'free'
          ? `${API}/public/aeo-scan/${scanId}`
          : `${API}/aeo-scans/${scanId}`;
      const headers = modeAtCall === 'free' ? publicHeader() : authHeader();
      const res = await axios.get(endpoint, { headers });
      const s = modeAtCall === 'free' ? res.data : res.data.scan;

      if (modeAtCall === 'free') {
        setResult({
          scanId: s.scanId,
          inputType: s.inputType,
          score: {
            aeoScore: s.score?.aeoScore ?? 0,
            geoScore: s.score?.geoScore ?? 0,
            aeoGrade: s.score?.aeoGrade ?? 'C',
            geoGrade: s.score?.geoGrade ?? 'C',
            summary: s.score?.summary ?? '',
            breakdown: s.score?.breakdown ?? {},
          },
          signals: s.signals || {},
          suggestions: Array.isArray(s.suggestions) ? s.suggestions : [],
          content: {
            title: s.content?.title ?? '',
            wordCount: s.content?.wordCount ?? 0,
            siteName: s.content?.siteName ?? null,
            byline: s.content?.byline ?? null,
          },
        });
        setUnlocked(!s.locked);
      } else {
        setResult({
          scanId: s.id,
          inputType: s.inputType,
          score: {
            aeoScore: s.aeoScore ?? 0,
            geoScore: s.geoScore ?? 0,
            aeoGrade: s.aeoGrade ?? 'C',
            geoGrade: s.geoGrade ?? 'C',
            summary: s.scores?.summary ?? '',
            breakdown: s.scores?.breakdown ?? {},
          },
          signals: s.signals || {},
          suggestions: s.suggestions?.list ?? [],
          content: {
            title: s.contentTitle ?? '',
            wordCount: s.wordCount ?? 0,
            siteName: s.meta?.siteName ?? null,
            byline: s.meta?.byline ?? null,
          },
        });
      }
    } catch {
      message.error('Failed to load scan');
      navigate(isPaid ? '/aeo-auditor' : '/aeo-auditor-public');
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (id && result?.scanId !== id) {
      loadScanDetails(id);
    } else if (!id) {
      setResult(null);
    }
  }, [id]);

  const handleScan = async () => {
    const payload: { url?: string; text?: string; recaptchaToken?: string } = {};
    if (activeTab === 'url') {
      const trimmed = url.trim();
      if (!trimmed) return message.warning('Enter a URL');
      payload.url = trimmed;
    } else {
      const trimmed = text.trim();
      if (!trimmed) return message.warning('Paste some content');
      if (trimmed.length < 100)
        return message.warning('Content too short — need 100+ characters');
      payload.text = trimmed;
    }

    setLoading(true);
    setUnlocked(false);
    const paidAtCall = isAuthenticated();
    try {
      if (!paidAtCall) {
        try {
          payload.recaptchaToken = await executeRecaptcha('aeo_auditor_scan');
        } catch {
          setLoading(false);
          return message.error('Security verification failed. Please try again.');
        }
      }
      const endpoint = paidAtCall ? `${API}/aeo-scan` : `${API}/public/aeo-scan`;
      const headers = paidAtCall ? authHeader() : publicHeader();
      const res = await axios.post(endpoint, payload, { headers });
      const d = res.data;

      if (!paidAtCall && d.locked && !Array.isArray(d.suggestions)) {
        d.suggestions = [];
      }

      message.success('AEO/GEO Auditing complete!');
      
      // Update result state before navigating.
      if (paidAtCall) {
        setResult({
          scanId: d.scanId,
          inputType: d.inputType,
          score: {
            aeoScore: d.score?.aeoScore ?? 0,
            geoScore: d.score?.geoScore ?? 0,
            aeoGrade: d.score?.aeoGrade ?? 'C',
            geoGrade: d.score?.geoGrade ?? 'C',
            summary: d.score?.summary ?? '',
            breakdown: d.score?.breakdown ?? {},
          },
          signals: d.signals || {},
          suggestions: d.suggestions || [],
          content: {
            title: d.content?.title ?? '',
            wordCount: d.content?.wordCount ?? 0,
            siteName: d.content?.siteName ?? null,
            byline: d.content?.byline ?? null,
          },
        });
      } else {
        setResult({
          scanId: d.scanId,
          inputType: d.inputType,
          score: {
            aeoScore: d.score?.aeoScore ?? 0,
            geoScore: d.score?.geoScore ?? 0,
            aeoGrade: d.score?.aeoGrade ?? 'C',
            geoGrade: d.score?.geoGrade ?? 'C',
            summary: d.score?.summary ?? '',
            breakdown: d.score?.breakdown ?? {},
          },
          signals: d.signals || {},
          suggestions: d.suggestions || [],
          content: {
            title: d.content?.title ?? '',
            wordCount: d.content?.wordCount ?? 0,
            siteName: d.content?.siteName ?? null,
            byline: d.content?.byline ?? null,
          },
        });
        setUnlocked(!d.locked);
      }

      setLoading(false);
      fetchHistory();
      const basePath = paidAtCall ? '/aeo-auditor' : '/aeo-auditor-public';
      navigate(`${basePath}/${d.scanId}`, { replace: true });
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'NO_AEO_CREDITS' || code === 'NO_AUDIT_CREDITS') {
        message.warning({
          content: 'No AEO scan credits remaining. Purchase add-ons in Account tab.',
          key: 'no-aeo-credits',
        });
      } else {
        message.error(err?.response?.data?.message || err?.message || 'Scan failed');
      }
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      const [{ pdf }, { default: AeoScanPdfReport }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./AeoScanPdfReport'),
      ]);

      const aeoQuality = getQualityTier(result.score.aeoGrade).label;
      const geoQuality = getQualityTier(result.score.geoGrade).label;
      const generatedAt = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      const blob = await pdf(
        <AeoScanPdfReport
          result={result}
          aeoQualityLabel={aeoQuality}
          geoQualityLabel={geoQuality}
          signalLabels={SIGNAL_LABELS}
          generatedAt={generatedAt}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aeo-geo-audit-${result.scanId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error('Failed to export PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleUnlockCheckout = async () => {
    if (!result) return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return message.warning('Enter a valid email');
    }
    setUnlockLoading(true);
    try {
      const res = await axios.post(
        `${API}/public/aeo-unlock`,
        { scanId: result.scanId, email: trimmed },
        { headers: publicHeader() }
      );
      const data = res.data || {};

      if (data.alreadyPurchased && data.unlockToken) {
        setShowEmailModal(false);
        setUnlockLoading(false);
        const params = new URLSearchParams({
          paid: '1',
          token: data.unlockToken,
          email: data.email || trimmed.toLowerCase(),
        });
        navigate(`/aeo-auditor-public/${result.scanId}?${params.toString()}`, { replace: true });
        message.info("You've already unlocked this report. PDF report sent to your email.", 5);
        return;
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('Unlock session URL failed');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Unlock checkout failed');
      setUnlockLoading(false);
    }
  };

  // Load unlocked data if returning from Stripe.
  useEffect(() => {
    if (isPaid) return;
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const emailParam = params.get('email');
    const paid = params.get('paid');
    const scanId = id;
    if (!paid || !token || !emailParam || !scanId) return;

    (async () => {
      try {
        const res = await axios.get(`${API}/public/aeo-report/${scanId}`, {
          params: { token, email: emailParam },
          headers: publicHeader(),
        });
        const d = res.data;
        setResult((prev) =>
          prev ? { ...prev, suggestions: d.suggestions || [], ...d } : d
        );
        setUnlocked(true);
        message.success('Audit report successfully unlocked!');
      } catch (err: any) {
        message.error('Unable to fetch unlocked report details');
      }
    })();
  }, [location.search, id, isPaid]);

  const isLocked = !isPaid && !unlocked;

  // Signal status evaluator helper
  const renderSignalStatus = (val: any) => {
    if (val === true || val === 'yes') {
      return <CheckCircleOutlined className="signal-status-icon status-pass" />;
    }
    if (val === false || val === 'no') {
      return <CloseCircleOutlined className="signal-status-icon status-fail" />;
    }
    if (val === null || val === undefined) {
      return <InfoCircleOutlined className="signal-status-icon status-info" />;
    }
    // numeric and counts
    return <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{val}</span>;
  };

  const getPriorityColorStyle = (p: string) => {
    switch (p) {
      case 'HIGH':
        return { '--prio-color': '#dc2626', color: '#dc2626', background: '#ffebeb' };
      case 'MEDIUM':
        return { '--prio-color': '#f59e0b', color: '#d97706', background: '#fef3c7' };
      default:
        return { '--prio-color': '#64748b', color: '#475569', background: '#f1f5f9' };
    }
  };

  return (
    <div className="aeo-scan-page">
      {/* ── Page Header ── */}
      <div className="aeo-scan-header-section">
        <div className="header-left">
          {result && (
            <div
              className="back-to-auditor-link"
              onClick={() => {
                navigate(isPaid ? '/aeo-auditor' : '/aeo-auditor-public');
                setUrl('');
                setText('');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13.5px',
                fontWeight: 600,
                color: '#ff7c02',
                cursor: 'pointer',
                marginBottom: '10px',
              }}
            >
              <ArrowLeftOutlined /> Back to Auditor
            </div>
          )}
          <h1 className={result ? 'header-title header-title--article' : 'header-title'}>
            <span className="header-title-text">
              {result ? result.content.title || 'Audited Content' : 'AEO/GEO Score Auditor'}
            </span>
          </h1>
          <p className="header-subtitle">
            {result ? (
              <>
                {result.inputType === 'url' ? 'Live URL scan' : 'Pasted-content scan'}
                {result.content.siteName && <> · <strong>{result.content.siteName}</strong></>}
                {result.content.byline && <> · by {result.content.byline}</>}
                {result.content.wordCount > 0 && <> · {result.content.wordCount.toLocaleString()} words</>}
              </>
            ) : (
              'Instantly verify search quality structures, AI crawlers, and E-E-A-T parameters.'
            )}
          </p>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {isPaid && !result && !loading && (
            <Button
              type="default"
              icon={<HistoryOutlined />}
              onClick={() => setHistoryModalOpen(true)}
              className="history-trigger-btn"
            >
              Audit History ({history.length})
            </Button>
          )}
          {result && !isLocked && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPdf}
              loading={pdfLoading}
              className="pdf-download-btn"
            >
              Download PDF
            </Button>
          )}
          {result && isLocked && (
            <Button
              type="primary"
              icon={<UnlockOutlined />}
              onClick={() => setShowEmailModal(true)}
              className="pdf-download-btn"
              style={{ background: '#ff7c02', borderColor: '#ff7c02' }}
            >
              Unlock Report
            </Button>
          )}
        </div>
      </div>

      {/* ── Input Panel ── */}
      {!result && !loading && (
        <div className="aeo-input-container">
          <Tabs
            activeKey={activeTab}
            onChange={(k) => setActiveTab(k as 'url' | 'text')}
            className="aeo-tabs"
            items={[
              {
                key: 'url',
                label: (
                  <span className="tab-label">
                    <LinkOutlined /> Analyze Live URL
                  </span>
                ),
                children: (
                  <div className="tab-content-block">
                    <Input
                      placeholder="https://example.com/blog-post"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="custom-url-input"
                    />
                    <span className="input-hint-text">
                      We will scrape the URL live and evaluate robots.txt, schema profiles, and layout patterns.
                    </span>
                  </div>
                ),
              },
              {
                key: 'text',
                label: (
                  <span className="tab-label">
                    <FileTextOutlined /> Analyze Text Content
                  </span>
                ),
                children: (
                  <div className="tab-content-block">
                    <Input.TextArea
                      placeholder="Paste your draft content here (min 100 characters)..."
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      rows={8}
                      className="custom-text-area"
                    />
                    <span className="input-hint-text">
                      Evaluates semantic structures, weak claims, stats density, readability, and FAQ signals directly form markup text.
                    </span>
                  </div>
                ),
              },
            ]}
          />
          <div className="aeo-action-bar">
            <Button type="primary" onClick={handleScan} className="run-audit-btn">
              Analyze Content
            </Button>
          </div>
          {!isPaid && (
            <p className="recaptcha-notice">
              This site is protected by reCAPTCHA and the Google{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{" "}
              apply.
            </p>
          )}
        </div>
      )}

      {/* ── Loading Card ── */}
      {loading && <ProgressiveLoadingCard mode={activeTab} />}

      {/* ── Results Dashboard ── */}
      {result && !loading && (
        <div className="aeo-scan-results-lock-wrapper">
          <div className="aeo-dashboard-layout">
            {/* Sidebar Column */}
            <div className="aeo-dash-sidebar">
              {/* Dual gauges component */}
              <div className="score-hero-container">
                {/* Gauge 1: AEO Score */}
                <div className="score-hero">
                  <h3 className="score-heading">AEO Score</h3>
                  <div className="gauge-wrap">
                    <ScoreGauge score={result.score.aeoScore} grade={result.score.aeoGrade} />
                  </div>
                  <div className="hero-score-display">
                    <span className="hero-score-num" style={{ color: getQualityTier(result.score.aeoGrade).color }}>
                      {Math.round(result.score.aeoScore)}
                    </span>
                    <span className="hero-score-suffix">/100</span>
                  </div>
                  <div
                    className="hero-score-grade"
                    style={{ background: getQualityTier(result.score.aeoGrade).color }}
                  >
                    {getQualityTier(result.score.aeoGrade).label}
                  </div>
                </div>

                {/* Gauge 2: GEO Score */}
                <div className="score-hero">
                  <h3 className="score-heading">GEO Score</h3>
                  <div className="gauge-wrap">
                    <ScoreGauge score={result.score.geoScore} grade={result.score.geoGrade} />
                  </div>
                  <div className="hero-score-display">
                    <span className="hero-score-num" style={{ color: getQualityTier(result.score.geoGrade).color }}>
                      {Math.round(result.score.geoScore)}
                    </span>
                    <span className="hero-score-suffix">/100</span>
                  </div>
                  <div
                    className="hero-score-grade"
                    style={{ background: getQualityTier(result.score.geoGrade).color }}
                  >
                    {getQualityTier(result.score.geoGrade).label}
                  </div>
                </div>
              </div>

              {/* Metadata Details Card */}
              <div className="aeo-metric-card">
                <h3>Audit Details</h3>
                <div className="document-info">
                  <div className="info-item">
                    <span className="info-lbl">Word Count</span>
                    <span className="info-val">{result.content.wordCount}</span>
                  </div>
                  {result.content.siteName && (
                    <div className="info-item">
                      <span className="info-lbl">Publisher</span>
                      <span className="info-val">{result.content.siteName}</span>
                    </div>
                  )}
                  {result.content.byline && (
                    <div className="info-item">
                      <span className="info-lbl">Byline</span>
                      <span className="info-val">{result.content.byline}</span>
                    </div>
                  )}
                  <div style={{ marginTop: '20px' }}>
                    <Button
                      block
                      onClick={() => {
                        navigate(isPaid ? '/aeo-auditor' : '/aeo-auditor-public');
                        setResult(null);
                        setUrl('');
                        setText('');
                      }}
                    >
                      Audit New Content
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Panel Content */}
            <div className="aeo-dash-main">
              {/* Critical UI Warning for noindex */}
              {result.signals.hasNoindex === true && (
                <div style={{
                  background: '#fef2f2', 
                  border: '1px solid #f87171', 
                  borderLeft: '4px solid #ef4444', 
                  padding: '16px 20px', 
                  borderRadius: '8px', 
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#991b1b', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>🚨</span> CRITICAL PENALTY TRIGGERED
                  </h3>
                  <p style={{ margin: 0, color: '#991b1b', fontSize: '14px', lineHeight: '1.5' }}>
                    We detected a <strong>noindex</strong> tag in your HTML. Search Engines and AI bots are blocked from reading this page. Your score has been capped at 20. <strong>Please remove the <code>noindex</code> tag to unlock your true score.</strong>
                  </p>
                </div>
              )}

              {/* Feedback Summary Card */}
              <div className="aeo-feedback-card">
                <div className="feedback-header">
                  <CheckCircleOutlined className="feedback-icon" />
                  <h3>Audit Assessment</h3>
                </div>
                <p className="feedback-body">{result.score.summary}</p>
              </div>

              {/* Checklist breakdown details grid */}
              <div className="aeo-signals-breakdown-card">
                <div className="breakdown-header">
                  <h3>Checklist Signal Verification</h3>
                </div>
                <div className="signals-grid">
                  {/* Category 1: Structure & Quality */}
                  <div className="signals-category-column">
                    <h4>Structure & Readability</h4>
                    
                    <div className="signal-row">
                      <div className="signal-info">
                        <span className="signal-name">Word Stats</span>
                        <span className="signal-val-sub">Recommended: 1000+ words</span>
                      </div>
                      {renderSignalStatus(result.signals.wordCount)}
                    </div>

                    <div className="signal-row">
                      <div className="signal-info">
                        <span className="signal-name">Headers Count</span>
                        <span className="signal-val-sub">Recommended: 4+ nested headers</span>
                      </div>
                      {renderSignalStatus(result.signals.headingCount)}
                    </div>

                    {result.inputType === 'url' && (
                      <div className="signal-row">
                        <div className="signal-info">
                          <span className="signal-name">Table of Contents</span>
                          <span className="signal-val-sub">Easy scrolling/section linking</span>
                        </div>
                        {renderSignalStatus(result.signals.hasToc)}
                      </div>
                    )}

                    <div className="signal-row">
                      <div className="signal-info">
                        <span className="signal-name">TL;DR Block</span>
                        <span className="signal-val-sub">Executive paragraph summarize</span>
                      </div>
                      {renderSignalStatus(result.signals.hasTldr)}
                    </div>

                    {result.inputType === 'url' && (
                      <div className="signal-row">
                        <div className="signal-info">
                          <span className="signal-name">Reading Time</span>
                          <span className="signal-val-sub">Estimated duration tag</span>
                        </div>
                        {renderSignalStatus(result.signals.hasReadingTime)}
                      </div>
                    )}

                    {result.inputType === 'url' && result.signals.urlMatchesH1 !== null && result.signals.urlMatchesH1 !== undefined && (
                      <div className="signal-row">
                        <div className="signal-info">
                          <span className="signal-name">URL ↔ H1 Alignment</span>
                          <span className="signal-val-sub">Slug keywords match page title</span>
                        </div>
                        {renderSignalStatus(result.signals.urlMatchesH1)}
                      </div>
                    )}

                    {result.inputType === 'url' && (
                      <div className="signal-row">
                        <div className="signal-info">
                          <span className="signal-name">Definition / Callout Blocks</span>
                          <span className="signal-val-sub">Blockquotes, callouts, or definitional prose</span>
                        </div>
                        {renderSignalStatus(result.signals.hasDefinitionBlocks)}
                      </div>
                    )}

                    {result.inputType === 'url' && (
                      <div className="signal-row">
                        <div className="signal-info">
                          <span className="signal-name">Hero / Featured Image</span>
                          <span className="signal-val-sub">First visible article image present</span>
                        </div>
                        {renderSignalStatus(result.signals.hasHeroImage)}
                      </div>
                    )}
                  </div>

                  {/* Category 2: LLM & AI access */}
                  <div className="signals-category-column">
                    <h4>AI Engine Access</h4>

                    {result.inputType === 'url' && (
                      <>
                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">robots.txt</span>
                            <span className="signal-val-sub">Correct domain routing</span>
                          </div>
                          {renderSignalStatus(result.signals.robotsTxtExists)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">Blocked crawlers</span>
                            <span className="signal-val-sub">Avoid blocking ChatGPT/Gemini</span>
                          </div>
                          {renderSignalStatus(Array.isArray(result.signals.blockedAiBots) ? result.signals.blockedAiBots.length === 0 : false)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">llms.txt</span>
                            <span className="signal-val-sub">Optimized text data summaries</span>
                          </div>
                          {renderSignalStatus(result.signals.hasLlmsTxt)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">Article Schema</span>
                            <span className="signal-val-sub">JSON-LD article validation</span>
                          </div>
                          {renderSignalStatus(result.signals.hasArticleSchema)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">FAQ Schema</span>
                            <span className="signal-val-sub">JSON-LD FAQ structural metadata</span>
                          </div>
                          {renderSignalStatus(result.signals.hasFaqSchema)}
                        </div>
                      </>
                    )}

                    {result.inputType === 'text' && (
                      <>
                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">Stats & Citations</span>
                            <span className="signal-val-sub">Contains statistics/credible claims</span>
                          </div>
                          {renderSignalStatus(result.signals.hasStatsInText)}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Category 3: Meta & Trust */}
                  <div className="signals-category-column">
                    <h4>E-E-A-T & Authority</h4>

                    {result.inputType === 'url' && (
                      <>
                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">Author Profile</span>
                            <span className="signal-val-sub">Biography links / context box</span>
                          </div>
                          {renderSignalStatus(result.signals.hasAuthorBio)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">Reviewer</span>
                            <span className="signal-val-sub">Peer-review validation box</span>
                          </div>
                          {renderSignalStatus(result.signals.hasReviewer)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">Meta Title</span>
                            <span className="signal-val-sub">Correct SEO headers length</span>
                          </div>
                          {renderSignalStatus(result.signals.hasMetaTitle)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">Meta Description</span>
                            <span className="signal-val-sub">Correct snippet description length</span>
                          </div>
                          {renderSignalStatus(result.signals.hasMetaDescription)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">FAQ Details</span>
                            <span className="signal-val-sub">Presents Q&A section</span>
                          </div>
                          {renderSignalStatus(result.signals.hasVisualFaq)}
                        </div>

                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">Descriptive Anchor Text</span>
                            <span className="signal-val-sub">Avoids generic "click here" / "read more" links</span>
                          </div>
                          {renderSignalStatus(result.signals.hasDescriptiveAnchorText)}
                        </div>

                        {result.signals.hasTechArticleSchema && (
                          <div className="signal-row">
                            <div className="signal-info">
                              <span className="signal-name">TechArticle Schema</span>
                              <span className="signal-val-sub">Advanced structured data subtype</span>
                            </div>
                            {renderSignalStatus(result.signals.hasTechArticleSchema)}
                          </div>
                        )}
                      </>
                    )}

                    {result.inputType === 'text' && (
                      <>
                        <div className="signal-row">
                          <div className="signal-info">
                            <span className="signal-name">FAQ Section</span>
                            <span className="signal-val-sub">Q&A formatting in draft</span>
                          </div>
                          {renderSignalStatus(result.signals.hasVisualFaq)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Actionable recommendations list */}
              <div className="aeo-improvements-card-wrapper">
                <div className="improvements-header">
                  <h3>Actionable Recommendations</h3>
                  <p>Resolving these items will directly improve your AEO visibility and GEO rankability indicators.</p>
                </div>
                <div className="improvements-list">
                  {isLocked ? (
                    <div
                      style={{
                        position: "relative",
                        padding: "40px 24px",
                        background: "#f8fafc",
                        border: "2px dashed #cbd5e1",
                        borderRadius: 12,
                        textAlign: "center",
                      }}
                    >
                      <div style={{ filter: "blur(6px)", opacity: 0.55, pointerEvents: "none", marginBottom: 24 }}>
                        <div style={{ background: "#fff", padding: 16, borderRadius: 8, marginBottom: 12, textAlign: "left" }}>
                          <strong>1. Optimize robots.txt rules</strong>
                          <p>Ensure that AI bots like GPTBot are not being blocked from crawling...</p>
                        </div>
                        <div style={{ background: "#fff", padding: 16, borderRadius: 8, marginBottom: 12, textAlign: "left" }}>
                          <strong>2. Fix Reviewer Signals</strong>
                          <p>Trust and E-E-A-T benefit significantly from having a visible peer-review box...</p>
                        </div>
                        <div style={{ background: "#fff", padding: 16, borderRadius: 8, textAlign: "left" }}>
                          <strong>3. Add Visual FAQ</strong>
                          <p>Structured Q&A sections help search engines digest answers quickly...</p>
                        </div>
                      </div>
                      <div style={{ position: "absolute", top: "50%", left: 0, right: 0, transform: "translateY(-50%)" }}>
                        <div style={{ fontSize: 44, marginBottom: 12 }}>🔒</div>
                        <h3 style={{ marginBottom: 8 }}>Detailed Audit Report Locked</h3>
                        <p style={{ color: "#64748b", maxWidth: 380, margin: "0 auto 16px" }}>
                          Purchase a one-time unlock to see E-E-A-T signals, crawler files, and detailed optimization tips to improve rankability by ChatGPT and Gemini.
                        </p>
                        <Button
                          type="primary"
                          size="large"
                          icon={<UnlockOutlined />}
                          style={{ background: "#ff7c02", borderColor: "#ff7c02" }}
                          onClick={() => setShowEmailModal(true)}
                        >
                          Unlock Detailed Results
                        </Button>
                      </div>
                    </div>
                  ) : result.suggestions.length === 0 ? (
                    <Empty description="No open suggestions — content hits all optimization parameters." />
                  ) : (
                    result.suggestions.map((imp, idx) => (
                      <div
                        key={idx}
                        className="improvement-row-card"
                        style={getPriorityColorStyle(imp.priority) as React.CSSProperties}
                      >
                        <div className="imp-card-head">
                          <span className="imp-signal-label">
                            {idx + 1}. {SIGNAL_LABELS[imp.signal] || imp.signal}
                          </span>
                          <span
                            className="imp-priority-badge"
                            style={{
                              color: getPriorityColorStyle(imp.priority).color,
                              background: getPriorityColorStyle(imp.priority).background,
                            }}
                          >
                            {imp.priority}
                          </span>
                        </div>
                        <p className="imp-fix-text">{imp.fix}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── FREEMIUM: Email capture modal → Stripe redirect ── */}
      <Modal
        title={
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              AEO/GEO Audit History
            </span>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 400 }}>
              {history.length} recent scan{history.length === 1 ? "" : "s"} ·
              Click a row to open the full report
            </span>
          </div>
        }
        open={historyModalOpen}
        onCancel={() => setHistoryModalOpen(false)}
        footer={null}
        width="min(1000px, 92vw)"
        centered
        className="history-modal"
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            padding: "16px 4px 4px",
          },
          header: {
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: 12,
          },
        }}
        closeIcon={
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "#f1f5f9",
              color: "#475569",
              fontSize: 14,
              transition: "all 0.15s ease",
            }}
          >
            ✕
          </span>
        }
      >
        {historyLoading ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 40 }}
          >
            <Spin />
          </div>
        ) : history.length === 0 ? (
          <Empty description="No audits saved yet." style={{ marginTop: 60 }} />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
              gap: 10,
              padding: "0 12px",
            }}
          >
            {history.map((h) => {
              const aeoVal = Math.round(h.aeoScore ?? 0);
              const geoVal = Math.round(h.geoScore ?? 0);

              const getScoreGrade = (score: number) => {
                return score >= 85 ? 'A' : score >= 70 ? 'B' : score >= 55 ? 'C' : score >= 40 ? 'D' : 'F';
              };

              const getScoreColor = (score: number) => {
                return getQualityTier(getScoreGrade(score)).color;
              };

              const getScoreLabel = (score: number) => {
                return getQualityTier(getScoreGrade(score)).label;
              };

              const aeoColor = getScoreColor(aeoVal);
              const geoColor = getScoreColor(geoVal);

              return (
                <div
                  key={h.id}
                  onClick={() => {
                    setHistoryModalOpen(false);
                    navigate(`/aeo-auditor/${h.id}`);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 12,
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    cursor: "pointer",
                    background: "#fff",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#ff7c02";
                    e.currentTarget.style.boxShadow = `0 2px 10px rgba(255, 124, 2, 0.1)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {/* AEO score pill */}
                  <div
                    style={{
                      width: 52,
                      minWidth: 52,
                      height: 52,
                      borderRadius: 10,
                      background: `${aeoColor}18`,
                      border: `1.5px solid ${aeoColor}`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: aeoColor,
                      }}
                    >
                      {aeoVal}
                    </span>
                    <span
                      style={{
                        fontSize: 8,
                        color: aeoColor,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        marginTop: 2,
                        textTransform: "uppercase",
                      }}
                    >
                      AEO
                    </span>
                  </div>

                  {/* GEO score pill */}
                  <div
                    style={{
                      width: 52,
                      minWidth: 52,
                      height: 52,
                      borderRadius: 10,
                      background: `${geoColor}18`,
                      border: `1.5px solid ${geoColor}`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: geoColor,
                      }}
                    >
                      {geoVal}
                    </span>
                    <span
                      style={{
                        fontSize: 8,
                        color: geoColor,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        marginTop: 2,
                        textTransform: "uppercase",
                      }}
                    >
                      GEO
                    </span>
                  </div>

                  {/* Meta */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginBottom: 4,
                      }}
                      title={h.contentTitle || "(Untitled Scan)"}
                    >
                      {h.contentTitle || "(Untitled Scan)"}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 11,
                        color: "#64748b",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: aeoColor,
                          background: `${aeoColor}14`,
                          padding: "1px 5px",
                          borderRadius: 3,
                          letterSpacing: 0.3,
                        }}
                      >
                        {getScoreLabel(aeoVal)}
                      </span>
                      <span>·</span>
                      <span>{h.inputType.toUpperCase()}</span>
                      <span>·</span>
                      <span>{h.wordCount} w</span>
                      <span>·</span>
                      <span>{new Date(h.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <ArrowRightOutlined
                    style={{ color: "#94a3b8", fontSize: 14 }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* ── Email Modal for Unlock Checkout ── */}
      <Modal
        title="Unlock Audit Report"
        open={showEmailModal}
        onCancel={() => setShowEmailModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowEmailModal(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={unlockLoading} onClick={handleUnlockCheckout}>
            Proceed to Checkout
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '10px 0' }}>
          <p style={{ margin: 0 }}>
            Enter your email to purchase the unlock. A link to the report and a PDF copy will be sent to this email address.
          </p>
          <Input
            placeholder="your.email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default AeoScan;
